import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import authService from '../services/authService';
import { getMyProfile } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isImpersonating = useRef(false);

  useEffect(() => {
    // impersonate 페이지에서는 초기 검증 건너뜀 (ImpersonateHandler가 직접 처리)
    if (window.location.pathname === '/auth/impersonate') {
      setLoading(false);
      return;
    }

    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      // jwt_token과 user가 일치하는지 검증 (이전 impersonate 잔존 토큰 정리)
      let validToken = sessionStorage.getItem('jwt_token') || localStorage.getItem('jwt_token');
      if (validToken) {
        try {
          const payload = JSON.parse(atob(validToken.split('.')[1]));
          if (payload.sub !== currentUser.username) {
            localStorage.removeItem('jwt_token');
            validToken = null;
          }
        } catch (e) {
          localStorage.removeItem('jwt_token');
          validToken = null;
        }
      }

      // 일단 로컬 유저 정보로 설정 (즉시 인증 상태)
      setUser(currentUser);

      // 유효한 토큰이 있을 때만 서버 검증
      if (validToken) {
        getMyProfile().then(res => {
          if (isImpersonating.current) return;
          const serverUser = res.data;
          localStorage.setItem('user', JSON.stringify(serverUser));
          setUser(serverUser);
        }).catch(() => {
          if (isImpersonating.current) return;
          localStorage.removeItem('user');
          localStorage.removeItem('jwt_token');
          sessionStorage.removeItem('user');
          setUser(null);
        }).finally(() => setLoading(false));
      } else {
        // 토큰 없음 → 로컬 user 정보만으로 유지 (다음 API 호출에서 401 시 처리됨)
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // api.js 인터셉터에서 발생하는 세션 만료 이벤트 수신
  useEffect(() => {
    const handleSessionExpired = () => {
      if (isImpersonating.current) return;
      setUser(null);
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  const login = async (username, password, sellerId) => {
    const userData = await authService.login(username, password, sellerId);
    setUser(userData);
    return userData;
  };

  /**
   * OAuth (카카오/네이버) 로그인 성공 후 AuthContext에 상태 반영
   */
  const loginWithOAuth = ({ accessToken, user: userData }) => {
    if (accessToken) {
      localStorage.setItem('jwt_token', accessToken);
    }
    setUser(userData);
    return userData;
  };

  const sellerLogin = async (username, password) => {
    const userData = await authService.sellerLogin(username, password);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    isImpersonating.current = false;
    await authService.logout();
    setUser(null);
  };

  const signup = async (userData) => {
    return await authService.signup(userData);
  };

  // 관리자 → 판매자 impersonate 전환
  const impersonate = (sellerData) => {
    isImpersonating.current = true;
    setUser(sellerData);
    setLoading(false);
  };

  const value = React.useMemo(() => ({
    user,
    login,
    loginWithOAuth,
    sellerLogin,
    logout,
    signup,
    impersonate,
    loading,
    isAuthenticated: !!user,
    isAdmin: user && user.role === 'ADMIN',
    isSeller: user && user.role === 'SELLER',
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
